<script setup>
import { ref, onMounted } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

// State
const users = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const editingId = ref(null);
const originalData = ref(null);

// Modal state
let messageModal = null;
const modalType = ref('error'); // 'error' | 'warning'
const modalMessages = ref([]);

// Delete state
const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);
const deleting = ref(false);

const form = ref({
    name: '',
    email: '',
    phone: '',
    location: '',
    password: '',
    password_confirmation: ''
});

// Modal Methods
const openErrorModal = (msg) => {
    modalMessages.value = Array.isArray(msg) ? msg : [msg];
    modalType.value = 'error';
    if (!messageModal) {
        messageModal = new bootstrap.Modal(document.getElementById('messageModal'));
    }
    messageModal.show();
};

const openWarningModal = (msg) => {
    modalMessages.value = Array.isArray(msg) ? msg : [msg];
    modalType.value = 'warning';
    if (!messageModal) {
        messageModal = new bootstrap.Modal(document.getElementById('messageModal'));
    }
    messageModal.show();
};

// Methods
const loadUsers = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/users/get');
        if (response.data.status === 'success') {
            users.value = response.data.data;
        }
    } catch (error) {
        console.error('Error loading users:', error);
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    form.value = {
        name: '',
        email: '',
        phone: '',
        location: '',
        password: '',
        password_confirmation: ''
    };
    isEditing.value = false;
    editingId.value = null;
    originalData.value = null;
};

const editUser = (user) => {
    form.value = {
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        location: user.location || '',
        password: '',
        password_confirmation: ''
    };
    isEditing.value = true;
    editingId.value = user.id;
    
    // Store original data for comparison
    originalData.value = {
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        location: user.location || ''
    };
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const submitForm = async () => {
    // Show spinner first
    saving.value = true;
    
    // Small delay to show spinner effect
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Client-side validation: check required fields
    const errors = [];
    if (!form.value.name || form.value.name.trim() === '') {
        errors.push('Nama wajib diisi.');
    }
    if (!form.value.email || form.value.email.trim() === '') {
        errors.push('Email wajib diisi.');
    }
    // For new user, password is required
    if (!isEditing.value) {
        if (!form.value.password || form.value.password.trim() === '') {
            errors.push('Password wajib diisi.');
        }
    }
    // Password confirmation check
    if (form.value.password && form.value.password !== form.value.password_confirmation) {
        errors.push('Konfirmasi password tidak cocok.');
    }
    
    if (errors.length > 0) {
        saving.value = false;
        openErrorModal(errors);
        return;
    }
    
    // Client-side validation: check for changes when editing
    if (isEditing.value && originalData.value) {
        const currentData = {
            name: form.value.name,
            email: form.value.email,
            phone: form.value.phone || '',
            location: form.value.location || ''
        };
        
        const hasTextChanges = JSON.stringify(originalData.value) !== JSON.stringify(currentData);
        const hasPasswordChange = form.value.password && form.value.password.trim() !== '';
        
        if (!hasTextChanges && !hasPasswordChange) {
            saving.value = false;
            openWarningModal('Tidak ada perubahan data yang disimpan.');
            return;
        }
    }
    
    let url = '/admin/users';
    const data = { ...form.value };
    
    if (isEditing.value) {
        data._method = 'PUT';
        url = `/admin/users/${editingId.value}`;
    }
    
    try {
        const response = await axios.post(url, data);
        if (response.data.status === 'success') {
            resetForm();
            loadUsers();
        }
    } catch (error) {
        if (error.response?.data?.errors) {
            const serverErrors = Object.values(error.response.data.errors).flat();
            openErrorModal(serverErrors);
        } else {
            openErrorModal(error.response?.data?.message || 'Terjadi kesalahan');
        }
    } finally {
        saving.value = false;
    }
};

// Delete Methods
const confirmDelete = (user) => {
    deleteId.value = user.id;
    deleteName.value = user.name;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = async () => {
    if (!deleteId.value || !deleteConfirmedCheck.value) return;
    
    deleting.value = true;
    try {
        const response = await axios.delete(`/admin/users/${deleteId.value}`);
        if (response.data.status === 'success') {
            // Close modal
            const modalEl = document.getElementById('deleteModal');
            const modal = bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();
            
            // Reload users
            loadUsers();
        } else {
            openErrorModal(response.data.message || 'Gagal menghapus user.');
        }
    } catch (error) {
        openErrorModal(error.response?.data?.message || 'Gagal menghapus user.');
    } finally {
        deleting.value = false;
    }
};

onMounted(() => {
    loadUsers();
});
</script>

<template>
    <Head title="Kelola User" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola User</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <Link href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></Link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">User</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Section -->
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary">
                                <i class="ti-user me-2"></i>{{ isEditing ? 'Edit User' : 'Tambah User Baru' }}
                            </h4>
                        </div>
                        <form @submit.prevent="submitForm">
                            <div class="box-body">
                                <div class="row">
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="user-name" class="form-label">Nama <span class="text-danger">*</span></label>
                                            <input type="text" id="user-name" name="name" v-model="form.name" class="form-control" placeholder="Nama lengkap" autocomplete="name">
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="user-email" class="form-label">Email <span class="text-danger">*</span></label>
                                            <input type="email" id="user-email" name="email" v-model="form.email" class="form-control" placeholder="alamat@email.com" autocomplete="email">
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="user-phone" class="form-label">Phone</label>
                                            <input type="text" id="user-phone" name="phone" v-model="form.phone" class="form-control" placeholder="Nomor telepon" autocomplete="tel">
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="user-location" class="form-label">Lokasi</label>
                                            <input type="text" id="user-location" name="location" v-model="form.location" class="form-control" placeholder="Kota/Wilayah" autocomplete="address-level2">
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="user-password" class="form-label">
                                                Password 
                                                <span v-if="!isEditing" class="text-danger">*</span>
                                                <small v-else class="text-muted">(Kosongkan jika tidak diubah)</small>
                                            </label>
                                            <input type="password" id="user-password" name="password" v-model="form.password" class="form-control" placeholder="Password" autocomplete="new-password">
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="user-password-confirm" class="form-label">Konfirmasi Password</label>
                                            <input type="password" id="user-password-confirm" name="password_confirmation" v-model="form.password_confirmation" class="form-control" placeholder="Ulangi password" autocomplete="new-password">
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button type="button" v-if="isEditing" @click="resetForm" class="btn btn-warning me-2">
                                    <i class="ti-close me-1"></i> Batal
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                    <i v-else class="ti-save-alt me-1"></i> Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- List Section -->
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title"><i class="ti-list me-2"></i>Daftar User</h4>
                        </div>
                        <div class="box-body p-0">
                            <div class="table-responsive">
                                <table class="table table-hover table-bordered mb-0">
                                    <thead class="bg-light">
                                        <tr>
                                            <th width="5%" class="text-center">#</th>
                                            <th width="20%">Nama</th>
                                            <th width="20%">Email</th>
                                            <th width="15%">Phone</th>
                                            <th width="15%">Last Login</th>
                                            <th width="10%" class="text-center">Status</th>
                                            <th width="15%" class="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading">
                                            <td colspan="7" class="text-center p-5">
                                                <div class="spinner-border text-primary"></div>
                                            </td>
                                        </tr>
                                        <tr v-else-if="users.length === 0">
                                            <td colspan="7" class="text-center text-muted p-5">
                                                <i class="fa fa-info-circle me-2"></i>Belum ada user
                                            </td>
                                        </tr>
                                        <tr v-else v-for="(user, index) in users" :key="user.id" :class="{'table-primary': editingId === user.id}">
                                            <td class="text-center align-middle">{{ index + 1 }}</td>
                                            <td class="align-middle">
                                                <strong>{{ user.name }}</strong>
                                                <div v-if="user.location" class="small text-muted">{{ user.location }}</div>
                                            </td>
                                            <td class="align-middle">{{ user.email }}</td>
                                            <td class="align-middle">{{ user.phone || '-' }}</td>
                                            <td class="align-middle">
                                                <span v-if="user.last_login_formatted">{{ user.last_login_formatted }}</span>
                                                <span v-else class="text-muted">Belum login</span>
                                            </td>
                                            <td class="text-center align-middle">
                                                <span :class="['badge', user.is_online ? 'badge-success' : (user.status_text === 'Aktif' ? 'badge-warning' : 'badge-secondary')]">
                                                    {{ user.status_text }}
                                                </span>
                                            </td>
                                            <td class="text-center align-middle">
                                                <button @click="editUser(user)" class="btn btn-sm btn-outline-primary me-1" title="Edit">
                                                    <i class="fa fa-edit me-1"></i> Edit
                                                </button>
                                                <button @click="confirmDelete(user)" class="btn btn-sm btn-outline-danger" title="Hapus">
                                                    <i class="fa fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Delete Confirmation Modal -->
            <div class="modal fade" id="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header bg-danger text-white">
                            <h5 class="modal-title" id="deleteModalLabel"><i class="fa fa-trash me-2"></i>Konfirmasi Hapus</h5>
                            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p>Apakah Anda yakin ingin menghapus user <strong>{{ deleteName }}</strong>?</p>
                            <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                            
                            <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                                <input class="form-check-input" type="checkbox" id="confirmDeleteCheck" v-model="deleteConfirmedCheck">
                                <label class="form-check-label text-dark" for="confirmDeleteCheck">
                                    Saya mengerti dan yakin ingin menghapus data ini.
                                </label>
                            </div>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                            <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck || deleting" @click="submitDelete">
                                <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
                                <i v-else class="fa fa-trash me-2"></i>Ya, Hapus
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Error/Warning Modal -->
            <div class="modal fade" id="messageModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                            <h5 class="modal-title">
                                <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                                {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                            </h5>
                            <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                            <p class="mb-3" v-else>Informasi sistem:</p>
                            <ul class="list-group list-group-flush">
                                <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(msg, index) in modalMessages" :key="index">
                                    <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ msg }}
                                </li>
                            </ul>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
